/**
 * Module availability, driven by PostHog feature flags.
 *
 * Every module can be gated by a PostHog feature flag named `module-<id>`
 * (e.g. `module-baby-tracker`). This is a layer ABOVE the user's own
 * enable/disable preference (`enabled_modules`): a flag decides whether the
 * module exists at all for that user — in the explore catalog, the dashboard
 * grid, the sidebar, and the route guard — while the preference decides
 * whether an available module is on their dashboard.
 *
 * Semantics (deliberately fail-open):
 *  - flag evaluates to `false`  → module hidden everywhere;
 *  - flag `true`, any multivariate value, or NO flag at all → module visible.
 * So no flags need to exist for the app to work: create `module-<id>` in
 * PostHog only when you want to control that module, and a PostHog outage or
 * ad-blocker can never blank the whole dashboard.
 *
 * Plumbing mirrors enabledModulesCookie.ts: posthog-js evaluates the flags on
 * the client (production only — see PostHogProvider), and the result is
 * mirrored into a cookie so middleware and server components know the
 * availability on every navigation without calling PostHog. No cookie yet
 * (first paint, dev, analytics off) → `null` → everything available.
 *
 * Like enabledModulesCookie.ts this module is free of registry/React/posthog
 * imports: the flag reader is injected, so the whole layer is unit-testable
 * (moduleFlags.test.ts) and cheap to pull into the edge middleware runtime.
 */

import { devSlotSuffix } from '@/shared/lib/devSlot'
import {
  ENABLED_MODULES_COOKIE_OPTIONS,
  parseEnabledModules,
  serializeEnabledModules,
} from '@/shared/lib/enabledModulesCookie'

/** PostHog flag key that gates a module: `module-<id>`. */
export function moduleFlagKey(moduleId: string): string {
  return `module-${moduleId}`
}

/**
 * The module ids whose flag does NOT evaluate to `false`. `getFlag` is
 * posthog's getFeatureFlag (or a fake in tests): `false` = flag exists and is
 * off for this user; `true`/variant string = on; `undefined` = no such flag
 * or flags unavailable — visible either way.
 */
export function resolveAvailableModuleIds(
  moduleIds: string[],
  getFlag: (flagKey: string) => unknown
): string[] {
  return moduleIds.filter((id) => getFlag(moduleFlagKey(id)) !== false)
}

/**
 * Restrict an enabled-module id list to the available set. `availableIds` is
 * `null` when availability is unknown (no cookie yet) — fail open and leave
 * the enabled set untouched.
 */
export function applyModuleAvailability(
  enabledIds: string[],
  availableIds: string[] | null
): string[] {
  if (availableIds == null) return enabledIds
  return enabledIds.filter((id) => availableIds.includes(id))
}

// Slot-suffixed in per-worktree dev for the same reason as `enabled_modules`
// (one localhost cookie jar shared by every slot — see devSlot.ts).
export const MODULE_FLAGS_COOKIE = `module_flags${devSlotSuffix()}`

// Same shape as the enabled-modules cookie: a cache of a rarely-changing
// value, rewritten every time posthog-js (re)loads the flags.
export const MODULE_FLAGS_COOKIE_OPTIONS = ENABLED_MODULES_COOKIE_OPTIONS

/** Decode the cookie value: available ids, or `null` when the cookie is absent. */
export function parseAvailableModules(
  value: string | null | undefined
): string[] | null {
  return parseEnabledModules(value)
}

/** Encode available ids as the compact comma-separated cookie value. */
export function serializeAvailableModules(ids: string[]): string {
  return serializeEnabledModules(ids)
}

/**
 * Client-only: persist the available ids so the next navigation's middleware
 * run (and the server layout's fast path) see the flag results without
 * calling PostHog. No-op on the server (no `document`).
 */
export function writeModuleFlagsCookie(ids: string[]): void {
  if (typeof document === 'undefined') return
  const { path, maxAge, sameSite } = MODULE_FLAGS_COOKIE_OPTIONS
  document.cookie = `${MODULE_FLAGS_COOKIE}=${serializeAvailableModules(
    ids
  )}; path=${path}; max-age=${maxAge}; SameSite=${sameSite}`
}

/** Client-only: drop the cookie (e.g. on sign-out) so per-user flag results can't leak to the next account. */
export function clearModuleFlagsCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${MODULE_FLAGS_COOKIE}=; path=${MODULE_FLAGS_COOKIE_OPTIONS.path}; max-age=0; SameSite=Lax`
}

/**
 * Client-only: read the current availability straight from `document.cookie`,
 * so client stores can start from the last known flag results instead of
 * flashing everything-available until posthog-js loads. `null` on the server
 * or when the cookie is absent.
 */
export function readModuleFlagsCookie(): string[] | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${MODULE_FLAGS_COOKIE}=`))
  if (!match) return null
  return parseAvailableModules(
    decodeURIComponent(match.slice(MODULE_FLAGS_COOKIE.length + 1))
  )
}
